import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button, ErrorText, Flex, FormField, Modal, TextField } from '@cmaas/ui';
import type { AdminProperty } from '../../api/types';
import { apiFetch, CmaasApiError } from '../../api/client';
import { useTenantOptions } from '../../api/useTenantOptions';

const createSchema = z.object({
  tenant_id: z.string().uuid('Choose a tenant'),
  name: z.string().min(1).max(255),
  apple_platform: z.enum(['legacy', 'uap', 'dual']),
  timezone: z.string().min(1),
});
type CreateValues = z.infer<typeof createSchema>;

const mutationError = (err: unknown) => (err instanceof CmaasApiError ? err.message : 'Something went wrong.');

export function NewPropertyModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const queryClient = useQueryClient();
  const key = ['properties'];
  const tenantOptions = useTenantOptions();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateValues>({ resolver: zodResolver(createSchema), defaultValues: { apple_platform: 'uap' } });

  const create = useMutation({
    mutationFn: (values: CreateValues) =>
      apiFetch<AdminProperty>('/v1/admin/properties', { method: 'POST', body: values }),
    onSuccess: (created) => {
      reset();
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ['tenant-properties', created.tenant_id] });
      onOpenChange(false);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
      create.reset();
    }
    onOpenChange(next);
  };

  const tenants = tenantOptions.data?.data ?? [];

  return (
    <Modal open={open} onOpenChange={handleOpenChange} title="New property">
      <form onSubmit={handleSubmit((v) => create.mutate(v))}>
        <Flex direction="column" gap="3" mt="4">
          <FormField label="Tenant" error={errors.tenant_id?.message}>
            <select {...register('tenant_id')}>
              <option value="">Select a tenant…</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Name" error={errors.name?.message}>
            <TextField.Root {...register('name')} />
          </FormField>
          <FormField label="Apple platform">
            <select {...register('apple_platform')}>
              <option value="legacy">legacy</option>
              <option value="uap">uap</option>
              <option value="dual">dual</option>
            </select>
          </FormField>
          <FormField label="Timezone (IANA)" error={errors.timezone?.message}>
            <TextField.Root {...register('timezone')} placeholder="America/New_York" />
          </FormField>
          {create.isError && <ErrorText>{mutationError(create.error)}</ErrorText>}
          <Flex gap="3" justify="end" mt="2">
            <Button type="button" variant="soft" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              Create property
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
}
